import { computed, inject } from '@angular/core';
import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withState,
} from '@ngrx/signals';
import { FeedbackService } from '@thiememeulenhoff/ng-api-gateway';
import {
  Feedback,
  FeedbackType,
  InteractionStatus,
  QuestionFeedback,
} from '@thiememeulenhoff/types';
import { firstValueFrom } from 'rxjs';
import { ResultEntry } from '../interaction';
import {
  toRequestError,
  withRequestStatus,
} from '../_utils/request-status.feature';

export type QuestionFeedbackFilterable = QuestionFeedback & {
  userIdentifier: string;
};

type FeedbackState = {
  questionFeedbacks: Array<QuestionFeedbackFilterable>;
  studentFeedbacks: Array<QuestionFeedbackFilterable>;
};

type TeacherFeedbackInput = {
  result: ResultEntry | undefined;
  isSelfTest: boolean;
  actor: string;
  arrangementCode: string;
  contentPath: string;
};

const initialState: FeedbackState = {
  questionFeedbacks: [],
  studentFeedbacks: [],
};

function convertFeedbackToQuestionFeedback(
  feedbacks: Array<Feedback>,
): Array<QuestionFeedbackFilterable> {
  return feedbacks.map(
    (feedback) =>
      ({
        id: feedback.id ?? feedback.createdAt,
        createdAt: feedback.createdAt,
        contentMessageType: 'message',
        header: 'assignment.feedback.teacher_message',
        message: feedback.message,
        type: FeedbackType.TEACHER_FEEDBACK,
        userIdentifier: feedback.userReference || feedback.actor || '',
      }) satisfies QuestionFeedbackFilterable,
  );
}

export const FeedbackStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withRequestStatus(),
  withComputed(({ questionFeedbacks }) => ({
    questionFeedbackByUser: computed(
      () => (requestedUserIdentifier: string | undefined) =>
        questionFeedbacks()
          .filter(
            ({ userIdentifier }) => userIdentifier === requestedUserIdentifier,
          )
          .toSorted(
            (a, b) =>
              new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
          ),
    ),
  })),
  withMethods((store, feedbackService = inject(FeedbackService)) => {
    let studentFeedbackRequestId = 0;

    return {
      async fetchFeedbackByTeacher({
        result,
        isSelfTest,
        actor,
        arrangementCode,
        contentPath,
      }: TeacherFeedbackInput): Promise<void> {
        patchState(store, { requestStatus: 'loading' });

        try {
          const feedbacks = await firstValueFrom(
            feedbackService.getFeedbackByTeacher(
              actor,
              arrangementCode,
              contentPath,
            ),
          );
          const questionFeedbacks =
            convertFeedbackToQuestionFeedback(feedbacks);

          if (
            result?.lastArchivedAt &&
            result.status === InteractionStatus.Empty &&
            !isSelfTest
          ) {
            questionFeedbacks.unshift({
              id: 'last-archived-at',
              createdAt: result.lastArchivedAt,
              contentMessageType: 'refresh',
              header: 'assignment.feedback.deleted_result',
              message: '',
              type: FeedbackType.ASSIGNMENT_DELETION,
              userIdentifier: actor,
            });
          }

          patchState(store, {
            questionFeedbacks,
            requestStatus: 'success',
          });
        } catch (error: unknown) {
          patchState(store, {
            requestStatus: toRequestError(error, 'Failed to fetch feedback'),
          });
        }
      },

      async fetchFeedbackByStudent(
        arrangementCode: string,
        contentPath: string,
      ): Promise<void> {
        studentFeedbackRequestId += 1;
        const requestId = studentFeedbackRequestId;
        patchState(store, { requestStatus: 'loading' });

        try {
          const feedbacks = await firstValueFrom(
            feedbackService.getFeedbackByStudent(arrangementCode, contentPath),
          );

          if (requestId === studentFeedbackRequestId) {
            patchState(store, {
              studentFeedbacks: convertFeedbackToQuestionFeedback(feedbacks),
              requestStatus: 'success',
            });
          }
        } catch (error: unknown) {
          if (requestId === studentFeedbackRequestId) {
            patchState(store, {
              requestStatus: toRequestError(error, 'Failed to fetch feedback'),
            });
          }
        }
      },

      async postFeedback(
        actor: string,
        arrangementCode: string,
        contentPath: string,
        message: string,
      ): Promise<void> {
        patchState(store, { requestStatus: 'loading' });

        try {
          await firstValueFrom(
            feedbackService.postFeedback(
              actor,
              arrangementCode,
              contentPath,
              message,
            ),
          );
          patchState(store, { requestStatus: 'success' });
        } catch (error: unknown) {
          patchState(store, {
            requestStatus: toRequestError(error, 'Failed to post feedback'),
          });
          throw error;
        }
      },
    };
  }),
);
